import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import '../common-styles.css';
import './LeadershipDetailPage.css';
import GradientHero from '../shared/GradientHero';
import nitinImg from '../../assets/images/leadership/nitinshingala.png';
import prakashImg from '../../assets/images/leadership/prakashshingala.png';
import jagdishImg from '../../assets/images/leadership/jagadishshingala.png';

const LeadershipDetailPage: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash]);

  return (
    <div className="leadership-detail-page">
      <GradientHero
        title={<><span>Guiding Vision.</span><br/>Inspiring Excellence.</>}
        backTo="/"
        badgeText="LEADERSHIPS"
        lead={<>At SunDiam, our leadership team brings together decades of experience, unwavering integrity, and a deep passion for diamonds. Their forward-thinking vision drives innovation, nurtures craftsmanship, and builds lasting relationships across global markets. With a firm belief in ethical practices and quality-first principles, our leaders shape the future of natural diamond excellence — one brilliant step at a time.</>}
        className="leadership-hero"
        titleClassName="leadership-primary-title"
        subtitleHighlight="Leadership"
        subtitleRest=" at SunDiam"
      />

      {/* Leadership Team Details */}
      <section className="leadership-details-section">
        <Container>
          {/* Jagdish Shingala */}
          <div id="jagdish-shingala" className="leadership-card mb-5">
            <Row className="align-items-stretch">
              <Col lg={7} xl={8} className="leadership-content d-flex flex-column">
                <div>
                  <h3 className="leader-name">Jagdish Shingala</h3>
                  <h4 className="leader-title">Founder & Chairman</h4>
                  <p className="leader-description">
                    The cornerstone of SunDiam, Jagdish Shingala is the visionary founder whose lifelong dedication laid the foundation of the company. With unmatched craftsmanship, relentless determination, and a deep love for natural diamonds, he transformed a humble beginning into a globally trusted name. Having spent decades building the SunDiam legacy from the ground up, he continues to shape the company's future with bold decisions and timeless wisdom. His values, integrity, and hands-on experience remain the guiding force behind every facet of SunDiam's growth. Today, as Chairman, he inspires the next generation with his enduring spirit and unwavering commitment to excellence.
                  </p>
                </div>
                <div className="mt-auto leadership-social">
                  <div className="leader-social-icons">
                    <button className="section-btn social-btn" aria-label="Instagram">
                      <span className="btn-content"><i className="fab fa-instagram"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="Facebook">
                      <span className="btn-content"><i className="fab fa-facebook"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="LinkedIn">
                      <span className="btn-content"><i className="fab fa-linkedin"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="Twitter">
                      <span className="btn-content"><i className="fab fa-twitter"></i></span>
                    </button>
                    <button className="section-btn social-btn email-btn" aria-label="Email">
                      <span className="btn-content"><i className="fas fa-envelope"></i> sundiam@email.com</span>
                    </button>
                  </div>
                </div>
              </Col>
              <Col lg={5} xl={4} className="leadership-image d-flex align-items-center order-2 order-lg-2">
                <div className="leader-image-wrapper yellow-bg">
                  <img src={jagdishImg} alt="Jagdish Shingala" className="leader-photo" />
                </div>
              </Col>
            </Row>
          </div>

          {/* Nitin Shingala */}
          <div id="nitin-shingala" className="leadership-card mb-5">
            <Row className="align-items-stretch">
                <Col lg={5} xl={4} className="leadership-image d-flex align-items-center order-2 order-lg-1">
                <div className="leader-image-wrapper purple-bg">
                  <img src={nitinImg} alt="Nitin Shingala" className="leader-photo" />
                </div>
              </Col>
                <Col lg={7} xl={8} className="leadership-content d-flex flex-column order-1 order-lg-2">
                <div>
                  <h3 className="leader-name">Nitin Shingala</h3>
                  <h4 className="leader-title">Chief Executive Officer (CEO)</h4>
                  <p className="leader-description">
                    A true craftsman at heart and a visionary in diamond manufacturing, Nitin Shingala leads SunDiam as CEO with a firm grip on operations and production. With years of experience in sourcing rough diamonds and overseeing their transformation, he plays a crucial dual role — as both a sharp rough buyer and a master of precision.
                    <br /><br />
                    Nitin manages the entire manufacturing unit with hands-on leadership, making strategic decisions that ensure efficiency, consistency, and quality. His deep understanding of rough materials and skill in identifying potential allows SunDiam to deliver brilliance from the very beginning. Under his guidance, the factory thrives not just as a production unit, but as a hub of craftsmanship, innovation, and trust.
                  </p>
                </div>
                <div className="mt-auto leadership-social">
                  <div className="leader-social-icons">
                    <button className="section-btn social-btn" aria-label="Instagram">
                      <span className="btn-content"><i className="fab fa-instagram"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="Facebook">
                      <span className="btn-content"><i className="fab fa-facebook"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="LinkedIn">
                      <span className="btn-content"><i className="fab fa-linkedin"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="Twitter">
                      <span className="btn-content"><i className="fab fa-twitter"></i></span>
                    </button>
                    <button className="section-btn social-btn email-btn" aria-label="Email">
                      <span className="btn-content"><i className="fas fa-envelope"></i> sundiam@email.com</span>
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Prakash Shingala */}
          <div id="prakash-shingala" className="leadership-card mb-5">
            <Row className="align-items-stretch">
              <Col lg={7} xl={8} className="leadership-content d-flex flex-column">
                <div>
                  <h3 className="leader-name">Prakash Shingala</h3>
                  <h4 className="leader-title">Chief Executive Officer (CEO)</h4>
                  <p className="leader-description">
                    A true gem in the world of natural diamonds, Prakash Shingala leads SunDiam as CEO with remarkable vision, integrity, and dedication. With decades of hands-on experience in diamond sales and retail relationships, he has been instrumental in shaping the company's global presence.
                    <br /><br />
                    Known for his deep understanding of the market and unwavering commitment to quality, Prakash brings a perfect balance of strategy and heart to the leadership table. His passion for the craft and strong business acumen have earned the trust of clients worldwide, making SunDiam a name synonymous with excellence.
                  </p>
                </div>
                <div className="mt-auto leadership-social">
                  <div className="leader-social-icons">
                    <button className="section-btn social-btn" aria-label="Instagram">
                      <span className="btn-content"><i className="fab fa-instagram"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="Facebook">
                      <span className="btn-content"><i className="fab fa-facebook"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="LinkedIn">
                      <span className="btn-content"><i className="fab fa-linkedin"></i></span>
                    </button>
                    <button className="section-btn social-btn" aria-label="Twitter">
                      <span className="btn-content"><i className="fab fa-twitter"></i></span>
                    </button>
                    <button className="section-btn social-btn email-btn" aria-label="Email">
                      <span className="btn-content"><i className="fas fa-envelope"></i> sundiam@email.com</span>
                    </button>
                  </div>
                </div>
              </Col>
              <Col lg={5} xl={4} className="leadership-image d-flex align-items-center order-2 order-lg-1">
                <div className="leader-image-wrapper">
                  <img src={prakashImg} alt="Prakash Shingala" className="leader-photo" />
                </div>
              </Col>
            </Row>
          </div>

          <div className="text-center mt-5">
            <Link to="/" className="back-link-inline">&larr; Back to Home</Link>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default LeadershipDetailPage;
